/**
 * Shown when the app opens in a meeting with no linked session and the
 * caller cannot link one from the sidebar:
 *
 *  - participants / co-hosts: the host hasn't started a session yet — wait.
 *  - the host, when their Zoom account isn't connected to a Facilitator
 *    Tools user (or Zoom couldn't confirm them as host): explain how to
 *    connect, or how to link the session from the web app using the
 *    meeting ID, then "Check again".
 */

import { openExternal, webBaseUrl } from '../lib/zoomActions';

interface UnlinkedScreenProps {
  isHost: boolean;
  meetingId?: string;
  onRetry: () => void;
}

/** 1234567890 → 123 456 7890 (Zoom's own grouping) for reading aloud / copying. */
function formatMeetingId(id?: string): string {
  if (!id) return '';
  const digits = id.replace(/\D/g, '');
  if (digits.length === 11) return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return id;
}

export function UnlinkedScreen({ isHost, meetingId, onRetry }: UnlinkedScreenProps) {
  const openIntegrationPage = () => {
    openExternal(`${webBaseUrl()}/integrations/zoom`).catch(() => {});
  };

  if (!isHost) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white px-6 text-center">
        <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mb-4">
          <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-900 mb-1">Waiting for the host</p>
        <p className="text-xs text-gray-500 mb-5">
          The host hasn&apos;t started a Facilitator Tools session in this meeting yet. Check back in a moment.
        </p>
        <button
          onClick={onRetry}
          className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
        >
          Check again
        </button>
      </div>
    );
  }

  const prettyId = formatMeetingId(meetingId);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white px-6 text-center">
      <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
      </div>
      <p className="text-sm font-medium text-gray-900 mb-1">No session is linked to this meeting yet</p>
      <p className="text-xs text-gray-500">
        To pick a session from this sidebar, connect your Zoom account to Facilitator Tools once: open
        Facilitator Tools, choose <b>Add to Zoom</b> and allow the app. Then come back and check again.
      </p>
      {prettyId && (
        <p className="text-xs text-gray-500 mt-3">
          Or link a session from its designer with <b>Link to meeting</b> and this meeting ID:
          <br />
          <span className="font-mono text-sm text-gray-900 select-all">{prettyId}</span>
        </p>
      )}
      <div className="flex gap-2 mt-5">
        <button
          onClick={openIntegrationPage}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
        >
          Open Facilitator Tools
        </button>
        <button
          onClick={onRetry}
          className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
        >
          Check again
        </button>
      </div>
    </div>
  );
}
